import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormGroupDirective, NgForm, Validators } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionPanel } from '@angular/material/expansion';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';

import {
  CustomValidators,
  EmailValidation,
  LocationNameValidation,
  MAX_DESCRIPTION_CHARS,
  PhoneNumberPattern,
  PhoneNumberValidator,
  UserDataValidation,
  ZipCodeValidation,
} from '@app/core/validations';
import { BusinessData, Countries, Establishment, ManageBusinessData, ProfilePreview, UserProfile } from '@app/api/models/api-models';
import { OPEN_STATUS } from '@app/api/models/openingTimes.models';
import { IHydraRestaurant } from './../../../api/models/hydra-api-models';
import { AddressValidationData } from '@app/api/models/userData';
import { BaseComponent } from '@app/core/abstract-classes/baseComponent';
import { DialogSize } from '@app/enum/dialogSize';
import { ADDRESS_ERROR, CITY_ERROR, COUNTRY_ERROR, ErrorToasterConfig, LOCATION_ERROR, POSTAL_CODE_ERROR } from '@app/core/utils/error-handle';

import { IUserDataService } from '@app/api/interfaces/userData/i.userdata.service';

import { ModalTermsConditionsComponent } from '../modal/model-term-conditions';
import { IOpenHoursService } from '@app/api/interfaces/openHours/i.openHours.service';
import { IAnalyticsService } from '@app/api/interfaces/analytics/i.analytics.service';

@Directive()
export class MyErrorStateMatcher extends BaseComponent implements ErrorStateMatcher {
  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    const invalidCtrl = !!(control && control.invalid && control.touched && control.parent.dirty);
    const invalidParent = !!(control && control.parent && control.parent.touched && control.parent.invalid && control.parent.dirty);

    return invalidCtrl || invalidParent;
  }
}

@Component({
  selector: 'app-cs-stepper',
  templateUrl: 'onboarding.component.html',
  styleUrls: ['./onboarding.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class OnboardingComponent extends BaseComponent implements OnInit, OnChanges, AfterViewChecked {
  firstFormGroup: FormGroup;
  secondFormGroup: FormGroup;
  thirdFormGroup: FormGroup;
  formConclusion: FormGroup;
  matcher = new MyErrorStateMatcher();
  isRegister = false;
  businessEmail = '';
  category = '';
  streetNumber = '';

  selectedOffering: string[] = [];
  selectedServices: string[] = [];
  selectedPayments: string[] = [];
  sortedPhoneNumberCodes: string[] = [];
  businessNameFilterError = false;
  addressFocus = false;
  showOpenHoursLayout = false;
  show = false;
  countryCode: string;
  userDataValidation = '';
  addressIsValidating = false;
  phoneNumberFocused = true;
  descriptionMaxChars = MAX_DESCRIPTION_CHARS;
  rendering = false;
  temporarilyClosedOpts = OPEN_STATUS;
  indexStep = 0;
  stepslist = [
    { title: 'csa.tells-more', step: 'csa.your-restaurante' },
    { title: 'csa.contact-info-second', step: 'csa.contact-info' },
    { title: 'csa.your-offer-title-second', step: 'csa.your-offer-title' },
    { title: 'csa.overview', step: 'csa.overview' },
  ];
  businessOpenTimes = [];
  businessSpecialOpenTimes = [];
  review: ProfilePreview;
  keyShowWeek = false;

  @Input() newBusiness: false;
  @Input() establishments: Establishment[];
  @Input() selectedEstablishmentId: string;
  @Input() userProfile: UserProfile;
  @Input() hasManageError: boolean;
  @Input() errorCodes: any;
  @Input() businessFromHydra: IHydraRestaurant;
  @Output() private registerEvent = new EventEmitter();
  @Output() private goToProfileEvent = new EventEmitter();

  @ViewChild('expansionPanel') myPanels: MatExpansionPanel;
  @ViewChild('address') addressInput: ElementRef;

  constructor(
    private formBuilder: FormBuilder,
    public dialog: MatDialog,
    private change: ChangeDetectorRef,
    private readonly translate: TranslateService,
    private snackBar: MatSnackBar,
    private userDataService: IUserDataService,
    private openHoursService: IOpenHoursService,
    private readonly analyticsService: IAnalyticsService
  ) {
    super();
    this.buildInitalFormGroup();
  }

  ngOnInit() {
    this.analyticsService.setDomain('wizard');
    this.analyticsService.setWebListingSection('basicInfo');
  }

  ngOnChanges(changes: SimpleChanges) {
    this.setOfferingsServicesAndCountryCodes();
    this.setAvailablePaymentMethods();
    this.checkForManageError();
    this.checkEstablishmentData();

    if (this.userProfile && this.userProfile.email) {
      this.businessEmail = this.userProfile.email;
      this.secondFormGroup.get('email').setValue(this.userProfile.email);
    }
    // disable so no update on customer can cause inconsistency
    this.secondFormGroup.get('email').disable();

    this.setAnalyticsParams();

    this.setDescriptionFromHydra(changes);

    this.firstFormGroup.markAllAsTouched();
  }

  ngAfterViewChecked() {
    if (this.addressFocus) {
      this.addressInput.nativeElement.focus();
      this.addressFocus = false;
    }
    this.change.detectChanges();
  }

  onBlur() {
    this.phoneNumberFocused = false;
  }

  onFocus() {
    this.phoneNumberFocused = true;
  }

  renderHours() {
    this.rendering = true;
  }

  private initEmptyFirstFormGroup(validators = this.getFormValidators()) {
    this.firstFormGroup = this.formBuilder.group({
      location: ['', validators.locationName],
      address: ['', validators.addressValidation],
      postal: ['', validators.postalCodeValidation],
      city: ['', validators.cityValidation],
      phone: ['', validators.phoneValidator],
      area: [''],
      country: ['', validators.countryValidation],
    });
    this.firstFormGroup.disable();
  }

  private checkEstablishmentData(): void {
    const validators = this.getFormValidators();

    if (this.establishments.length > 0) {
      const selectedEstablishment = this.establishments.find(x => x.establishmentId === this.selectedEstablishmentId);
      this.establishmentCountryCode = selectedEstablishment?.countryCode;
      this.countryCode = selectedEstablishment?.countryCode;
      this.zipCodeRegex = this.getCountryZipCodeRegex(this.countryCode);

      if (selectedEstablishment) {
        const selectedCountry = this.categoriesAndCountries.countries.find(c => c.countryCode === this.countryCode);
        if (selectedCountry) {
          this.setFirstFromGroup(selectedEstablishment, selectedCountry);
          return;
        }
      }

      // fallback
      this.initEmptyFirstFormGroup(validators);
    } else {
      this.initEmptyFirstFormGroup(validators);
    }
  }

  private getFormValidators(countryCode: string = '+49') {
    return {
      addressValidation: Validators.compose([Validators.required, UserDataValidation(ADDRESS_ERROR, this.userDataValidation)]),
      postalCodeValidation: Validators.compose([Validators.required, UserDataValidation(POSTAL_CODE_ERROR, this.userDataValidation)]),
      cityValidation: Validators.compose([Validators.required, UserDataValidation(CITY_ERROR, this.userDataValidation)]),
      countryValidation: Validators.compose([Validators.required, UserDataValidation(COUNTRY_ERROR, this.userDataValidation)]),
      locationName: Validators.compose([...LocationNameValidation, UserDataValidation(LOCATION_ERROR, this.userDataValidation)]),
      phoneValidator: Validators.compose([...PhoneNumberPattern, Validators.required, PhoneNumberValidator(countryCode)]),
    };
  }

  /**
   * Sets the firstFromGroup
   * @param establishment Establishment to retrieve the data from
   * @param parsedPhone ParsedPhone object which contains the phoneNumber and the phoneNumberCountryCode
   * @param country Country object which holds the name of the country
   */
  private setFirstFromGroup(establishment: Establishment, country: Countries): void {
    const { name, street, postalCode, city, phone } = establishment;

    const validators = this.getFormValidators(country.code);
    const parsedPhoneNumber = this.parsePhoneNumber(country, phone);

    this.firstFormGroup = this.formBuilder.group({
      location: [{ value: name, disabled: true }, validators.locationName],
      address: [{ value: street, disabled: false }, validators.addressValidation],
      postal: [{ value: postalCode, disabled: false }, validators.postalCodeValidation],
      city: [{ value: city, disabled: false }, validators.cityValidation],
      phone: [{ value: parsedPhoneNumber?.phoneNumber ?? '', disabled: false }, validators.phoneValidator],
      area: [{ value: parsedPhoneNumber?.phoneNumberCountryCode ?? '', disabled: false }],
      country: [{ value: country.name ? country.name : '', disabled: true }, validators.countryValidation],
    });

    this.storeAcceptedAddressState();
  }

  /**
   * Checks if is there a manage error and reacts to it
   */
  private checkForManageError(): void {
    if (this.hasManageError) {
      const errorsToShow = this.errorCodes && this.errorCodes instanceof Array ? this.errorCodes : ['csa.onboarding-failed-message'];
      const translatedText = Object.values(this.translate.instant(errorsToShow));
      this.snackBar.open(translatedText.toString(), '', ErrorToasterConfig);
    }
  }

  /**
   * Checks if the categoriesAndCountries object is available and sets the availableOfferings, availableServices, availablePaymentMethods and sortedPhoneNumberCodes
   * @param changes SimpleChanges object
   */
  private setOfferingsServicesAndCountryCodes(): void {
    if (this.categoriesAndCountries && !this.availableOfferings && this.establishmentCategory) {
      const { services, countries } = this.categoriesAndCountries;
      this.setAvailableOfferings();
      this.setAvailableServices(services);
      this.sortedPhoneNumberCodes = this.sortPhoneNumberCountryCodes(countries);
    }
  }

  /**
   * Handles opening times and special changes
   */
  onOpeningTimesChange() {
    this.businessOpenTimes = this.openHoursService.getOpenHoursToSave();
    this.businessSpecialOpenTimes = this.openHoursService.getSpecialOpenHoursToSave();
  }

  /**
   * Handles Offerings Change
   */
  onOfferingsChange(offerings: string[]) {
    this.selectedOffering = [...offerings];
    this.validateCategories();
  }

  /**
   * Handles payment Change
   */
  onPaymentsChange(payments: string[]) {
    this.selectedPayments = [...payments];
    this.validateCategories();
  }

  /**
   * Handles service Change
   */
  onServicesChange(services: string[]) {
    this.selectedServices = [...services];
    this.validateCategories();
  }

  /*
   * Method to initalize form groups for each step.
   */
  private buildInitalFormGroup() {
    /**Form group validations */
    const addressValidation = Validators.compose([Validators.required, UserDataValidation(ADDRESS_ERROR, this.userDataValidation)]);
    const postalCodeValidation = Validators.compose([...ZipCodeValidation, UserDataValidation(POSTAL_CODE_ERROR, this.userDataValidation)]);
    const cityValidation = Validators.compose([Validators.required, UserDataValidation(CITY_ERROR, this.userDataValidation)]);
    const countryValidation = Validators.compose([Validators.required, UserDataValidation(COUNTRY_ERROR, this.userDataValidation)]);
    const locationName = Validators.compose([...LocationNameValidation, UserDataValidation(LOCATION_ERROR, this.userDataValidation)]);

    this.firstFormGroup = this.formBuilder.group({
      location: ['', locationName],
      address: ['', addressValidation],
      postal: new FormControl('', postalCodeValidation),
      city: ['', cityValidation],
      phone: ['', [...PhoneNumberPattern, PhoneNumberValidator('+49')]],
      area: ['+49'],
      country: ['Germany', countryValidation],
    });

    this.secondFormGroup = this.formBuilder.group({
      email: [this.businessEmail, EmailValidation],
      contactEmail: ['', EmailValidation],
      website: [
        '',
        Validators.compose([
          CustomValidators.patternValidator({
            invalidSite: true,
          }),
        ]),
      ],
      description: [
        this.businessFromHydra ? this.businessFromHydra.description : '',
        Validators.compose([
          CustomValidators.descriptionValidator(),
          CustomValidators.descriptionWithUrl(),
          CustomValidators.noLeadingTrailingSpacesValidator(),
          CustomValidators.noMultipleSpacesValidator(),
        ]),
      ],
    });

    this.thirdFormGroup = this.formBuilder.group({
      hasSelection: ['', Validators.required],
    });
  }

  /**
   * Event fired when user goes to main page.
   */
  goToProfile() {
    this.goToProfileEvent.emit();
  }

  async goToStepContactInfo() {
    //stepper: MatStepper
    if (this.hasForeignCountrySelection()) {
      this.applyCountryMismatchError();
      return;
    }

    this.showOpenHoursLayout = true;

    const addressValidationData: AddressValidationData = {
      establishmentId: this.selectedEstablishmentId,
      languageCode: this.translate.currentLang,
      countryCode: this.setCountryCode(this.firstFormGroup.get('country').value),
      zipCode: this.firstFormGroup.get('postal').value,
      street: this.firstFormGroup.get('address').value,
      category: this.establishmentCategory,
      locationName: this.firstFormGroup.get('location').value,
      additional: '',
      city: this.firstFormGroup.get('city').value,
    };
    this.addressIsValidating = true;
    this.userDataService.validateAddress(addressValidationData).subscribe(result => {
      const { errorCode, status } = result;
      if (errorCode || status) {
        const translatedText = errorCode ? this.translate.instant(`csa.${errorCode.toLowerCase()}`) : this.translate.instant('csa.invalid_address');
        this.snackBar.open(translatedText.toString(), '', ErrorToasterConfig);
        this.userDataValidation = errorCode ? errorCode : status.toString();
      } else {
        this.userDataValidation = '';
        //stepper.next()
        //this.analyticsService.setWebListingSection('contactInfo')
        //this.analyticsService.setPageName()
        this.setStepperIndex(1);
      }
      this.addressIsValidating = false;
    });
  }

  /**
   * This method save the claim and registration information.
   */
  save(form: FormGroup, secondFormGroup: FormGroup, formConclusion: FormGroup) {
    const claim = this.createClaimToSave(form.getRawValue(), secondFormGroup.getRawValue());
    this.registerEvent.emit(claim);
  }

  /**
   * This method creates the object dto for the middlware service
   */
  private createClaimToSave(firstForm: any, secondFormGroup: any) {
    const openHours = this.openHoursService.getOpenHoursToSave();
    const specialOpenHours = this.openHoursService.getSpecialOpenHoursToSave();
    const tempClosed = this.openHoursService.getTempClosed();

    const claimData: BusinessData = {
      userFirstName: this.userProfile.firstName,
      userLastName: this.userProfile.lastName,
      channels: null,
      name: firstForm.location,
      additional: '',
      street: firstForm.address,
      zipCode: firstForm.postal,
      city: firstForm.city,
      establishmentId: this.selectedEstablishmentId,
      countryCode: this.setCountryCode(firstForm.country),
      languageCode: this.translate.currentLang,
      description: secondFormGroup.description,
      url: secondFormGroup.website,
      contactEmail: secondFormGroup.contactEmail,
      contactPhoneNumber: firstForm.phone !== '' ? firstForm.area.replace(' ', '') + firstForm.phone : '',
      openingTimes: openHours,
      specialOpeningTimes: specialOpenHours,
      offers: this.selectedOffering,
      category: this.establishmentCategory,
      services: this.selectedServices,
      paymentMethods: this.selectedPayments,
      openStatus: tempClosed,
      businessEmail: secondFormGroup.contactEmail,
    };

    const manageBusinessData: ManageBusinessData = {
      data: claimData,
      channels: ['GOOGLE_MY_BUSINESS'],
    };

    return manageBusinessData;
  }

  /**
   * Method to build information retrieved form google places api.
   */
  setAddress(addrObj: any): void {
    if (!addrObj) {
      return;
    }
    const { address, postal_code, locality, country, street_number, phone_number, website } = addrObj;

    if (this.isCountryMismatch(country)) {
      this.applyCountryMismatchError();
      this.restoreAcceptedAddressState(this.secondFormGroup);
      return;
    }

    this.clearCountryMismatchError();
    this.firstFormGroup.get('address').setValue(address);
    this.firstFormGroup.get('postal').setValue(postal_code);
    this.firstFormGroup.get('city').setValue(locality);
    this.firstFormGroup.get('country').setValue(country);
    this.streetNumber = street_number ? street_number : '';

    this.setCountryCode(country);
    this.setZipCodeRegex(this.countryCode);

    const regexPhone = phone_number.replace(/^(049|0+)?/g, '');
    this.firstFormGroup.get('phone').setValue(regexPhone);
    this.secondFormGroup.get('website').setValue(website);

    this.addressFocus = true;
    this.addressInput.nativeElement.focus();

    this.setAreaCode(country);
    this.storeAcceptedAddressState(this.secondFormGroup);
  }

  private applyCountryMismatchError(): void {
    this.countryMismatchError = true;
    this.firstFormGroup?.get('address')?.setErrors({ countryMismatch: true });
    this.firstFormGroup?.get('address')?.markAsTouched();
    this.firstFormGroup?.get('country')?.markAsTouched();
    this.snackBar.open(this.translate.instant('csa.yext_invalid_address_country_code').toString(), '', ErrorToasterConfig);
  }

  private validateCategories(): void {
    const categoriesAreValid = !!(this.selectedOffering.length || this.selectedPayments.length || this.selectedServices.length);
    this.thirdFormGroup.controls.hasSelection.setValue(categoriesAreValid ? 'checked' : '');
  }

  /**
   * Show modal information if categories step is not validated.
   * @param event
   */
  steperchange(event: any) {
    //this.analyticsService.setWebListingSection('overview')
    if (this.thirdFormGroup.controls.hasSelection.invalid) {
      //this.analyticsService.setWebListingSection('offers')
      //this.setStepperIndex(3)
      this.dialog.open(ModalTermsConditionsComponent, {
        data: { isOffersValidation: true },
        width: DialogSize.SMALL,
        panelClass: 'term-condiitons-panel',
      });
    } else {
      this.setStepperIndex(3);
    }
    //this.analyticsService.setPageName()
    return null;
  }

  setStepperStateAnalytics(step: string) {
    this.analyticsService.setWebListingSection(step);
    this.analyticsService.setPageName();
  }

  setStepperIndex(index: number) {
    if (this.openHoursService.getOpenHoursToSave()) {
      this.setGoogleReview();
    }
    this.keyShowWeek = false;
    this.indexStep = index;
  }

  async setGoogleReview() {
    const openHours = this.openHoursService.getOpenHoursToSave();
    const first = this.firstFormGroup.getRawValue();
    const second = this.secondFormGroup.getRawValue();
    const week = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const today = openHours[week[new Date().getDay()]];

    let translatedOfferings: string[] = [];
    if (this.selectedOffering.length > 0) {
      translatedOfferings = Object.values(await this.translate.get(this.selectedOffering).toPromise());
    }
    this.review = {
      name: first.location,
      description: second.description,
      street: `${first.address}, ${first.city}`,
      hour: [],
      openStatus: 'Closed',
      openColor: 'red',
      now: '',
      phone: first.phone !== '' ? first.area.replace(' ', '') + first.phone : '',
      url: second.website,
      offerings: translatedOfferings.join(', '),
    };

    //check if it is open
    if (today[0]) {
      this.review.now = `${today[0].startTime} - ${today[0].endTime}`;
      const startHour = parseInt(today[0].startTime.slice(0, 2), 10);
      const startMin = parseInt(today[0].startTime.slice(-2), 10);
      const endHour = parseInt(today[0].endTime.slice(0, 2), 10);
      const endMin = parseInt(today[0].endTime.slice(-2), 10);
      const nowHour = new Date().getHours();
      const nowMin = new Date().getMinutes();

      const isOpen = 'Open:';
      if (startHour <= nowHour && nowHour <= endHour) {
        if (startHour == nowHour && startMin < nowMin) {
          this.review.openColor = 'green';
          this.review.openStatus = isOpen;
        }
        if (endHour == nowHour && nowMin < endMin) {
          this.review.openColor = 'green';
          this.review.openStatus = isOpen;
        }
        if (startHour != nowHour && nowMin != endHour) {
          this.review.openColor = 'green';
          this.review.openStatus = isOpen;
        }
      }
    }

    //Set All week
    this.review.hour = [];
    week.forEach(e => {
      openHours[e][0]
        ? this.review.hour.push({ day: e, hour: `${openHours[e][0].startTime} - ${openHours[e][0].endTime}` })
        : this.review.hour.push({ day: e, hour: 'Close' });
    });
  }
  showWeek() {
    this.keyShowWeek = !this.keyShowWeek;
  }

  /**
   * Sets the description field if it exists on hydra establishment data
   * @param changes onChanges Event to guarantee that the description is only set once
   */
  private setDescriptionFromHydra(changes: SimpleChanges): void {
    if (
      this.secondFormGroup &&
      this.businessFromHydra &&
      this.businessFromHydra.description &&
      changes.businessFromHydra &&
      changes.businessFromHydra.previousValue !== changes.businessFromHydra.currentValue
    ) {
      this.secondFormGroup.controls['description'].setValue(this.businessFromHydra.description);
    }
  }

  private setAnalyticsParams() {
    const productVersion = 'basic';
    const selectedEstablishment = this.establishments.find(x => x.establishmentId === this.selectedEstablishmentId);
    const { name, countryCode, postalCode, category } = selectedEstablishment;

    this.analyticsService.setUserCountry(countryCode);
    this.analyticsService.setEstablishmentCountry(countryCode);
    this.analyticsService.setEstablishmentId(this.selectedEstablishmentId);
    this.analyticsService.setEstablishmentZipCode(postalCode);
    this.analyticsService.setEstablishmentName(name);
    this.analyticsService.setEstablishmentCategory(category);
    this.analyticsService.setProductVersion(productVersion);
    this.analyticsService.setPageName();
  }
}
